import type { DatabaseType } from "@repo/lanes";
import type { LogoIconName } from "@repo/ui/components/icon";
import { z } from "zod";

import { useAppForm } from "~/components/form";

export type DatasourceTypeKey = DatabaseType;

export interface DatasourceTypeOption {
  value: DatasourceTypeKey;
  label: string;
  description: string;
  logo: LogoIconName;
}

export const DATASOURCE_TYPE_OPTIONS: DatasourceTypeOption[] = [
  {
    value: "POSTGRESQL",
    label: "PostgreSQL",
    description: "Open-source relational database.",
    logo: "postgresql",
  },
  {
    value: "MYSQL",
    label: "MySQL",
    description: "Popular open-source relational database.",
    logo: "mysql",
  },
  {
    value: "BIGQUERY",
    label: "BigQuery",
    description: "Google's serverless, highly scalable data warehouse.",
    logo: "bigquery",
  },
  {
    value: "CLICKHOUSE",
    label: "ClickHouse",
    description: "Column-oriented database for real-time analytics.",
    logo: "clickhouse",
  },
  {
    value: "REDSHIFT",
    label: "Redshift",
    description: "Amazon's cloud data warehouse service.",
    logo: "redshift",
  },
  {
    value: "SNOWFLAKE",
    label: "Snowflake",
    description: "Cloud-native data warehouse with elastic scaling.",
    logo: "snowflake",
  },
  {
    value: "DATABRICKS",
    label: "Databricks",
    description: "Unified platform for data, analytics and AI..",
    logo: "databricks",
  },
];

// ---------------------------------------------------------------------------
// Per-driver form schemas
// ---------------------------------------------------------------------------

const nameField = z.string().min(1, "Datasource name is required");

/** How the user authenticates against Snowflake. Form-only — never sent to the API. */
export const SnowflakeAuthMethodSchema = z.enum(["password", "keypair"]);
export type SnowflakeAuthMethod = z.infer<typeof SnowflakeAuthMethodSchema>;

const sqlBaseSchema = z.object({
  name: nameField,
  host: z.string().min(1, "Host is required"),
  port: z.coerce.number({ message: "Port must be a number" }),
  database: z.string().min(1, "Database is required"),
  user: z.string().min(1, "User is required"),
  password: z.string().min(1, "Password is required"),
  ssl: z.boolean().optional(),
});

const postgresSchema = sqlBaseSchema.extend({
  type: z.literal("POSTGRESQL"),
});

const mysqlSchema = sqlBaseSchema.extend({
  type: z.literal("MYSQL"),
});

const redshiftSchema = sqlBaseSchema.extend({
  type: z.literal("REDSHIFT"),
});

const bigquerySchema = z.object({
  name: nameField,
  type: z.literal("BIGQUERY"),
  // Parseability is checked here because the API's `LanesConfigSchema` parses
  // the string into an object; malformed JSON should surface as an inline
  // field error rather than a failed request.
  serviceAccount: z
    .string()
    .min(1, "Service account JSON is required")
    .refine(
      (value) => {
        try {
          return typeof JSON.parse(value) === "object";
        } catch {
          return false;
        }
      },
      { message: "Must be valid service account JSON" },
    ),
});

const clickhouseSchema = z.object({
  name: nameField,
  type: z.literal("CLICKHOUSE"),
  url: z.string().min(1, "URL is required"),
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  chDatabase: z.string().optional(),
});

const snowflakeSchema = z
  .object({
    name: nameField,
    type: z.literal("SNOWFLAKE"),
    account: z.string().min(1, "Account is required"),
    warehouse: z.string().min(1, "Warehouse is required"),
    database: z.string().min(1, "Database is required"),
    schemas: z.array(z.string()).min(1, "Select at least one schema"),
    username: z.string().min(1, "Username is required"),
    authMethod: SnowflakeAuthMethodSchema,
    password: z.string().optional(),
    privateKey: z.string().optional(),
    privateKeyPass: z.string().optional(),
    role: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.authMethod === "password" && !v.password) {
      ctx.addIssue({
        code: "custom",
        message: "Password is required",
        path: ["password"],
      });
    }
    if (v.authMethod === "keypair" && !v.privateKey) {
      ctx.addIssue({
        code: "custom",
        message: "Private key is required",
        path: ["privateKey"],
      });
    }
  });

const databricksSchema = z.object({
  name: nameField,
  type: z.literal("DATABRICKS"),
  host: z.string().min(1, "Host is required"),
  path: z.string().min(1, "HTTP path is required"),
  token: z.string().min(1, "Access token is required"),
  catalog: z.string().min(1, "Catalog is required"),
});

export const datasourceFormSchema = z.discriminatedUnion("type", [
  postgresSchema,
  mysqlSchema,
  redshiftSchema,
  bigquerySchema,
  clickhouseSchema,
  snowflakeSchema,
  databricksSchema,
]);

export type DatasourceFormValues = z.infer<typeof datasourceFormSchema>;

// ---------------------------------------------------------------------------
// Flat default values (superset of all driver fields)
// ---------------------------------------------------------------------------

export interface DatasourceFormDefaults {
  name: string;
  type: DatasourceTypeKey | "";
  // SQL-based (postgres/mysql/redshift)
  host: string;
  port: string;
  database: string;
  user: string;
  password: string;
  ssl: boolean;
  // BigQuery
  serviceAccount: string;
  // ClickHouse
  url: string;
  username: string;
  chDatabase: string;
  // Snowflake
  account: string;
  warehouse: string;
  schemas: string[];
  role: string;
  authMethod: SnowflakeAuthMethod;
  privateKey: string;
  privateKeyPass: string;
  // Databricks
  path: string;
  token: string;
  catalog: string;
}

const FORM_DEFAULTS: DatasourceFormDefaults = {
  name: "",
  type: "",
  host: "",
  port: "",
  database: "",
  user: "",
  password: "",
  ssl: false,
  serviceAccount: "",
  url: "",
  username: "",
  chDatabase: "",
  account: "",
  warehouse: "",
  schemas: [],
  role: "",
  authMethod: "password",
  privateKey: "",
  privateKeyPass: "",
  path: "",
  token: "",
  catalog: "",
};

// ---------------------------------------------------------------------------
// Form hook
// ---------------------------------------------------------------------------

/** Hook wrapper so we can derive the form instance type via `ReturnType`. */
export const useDatasourceForm = (
  onSubmit: (value: DatasourceFormDefaults) => Promise<void>,
) =>
  useAppForm({
    defaultValues: FORM_DEFAULTS,
    validators: {
      // The discriminated union validates the correct branch based on `type`;
      // zod strips extra flat-form fields at runtime. The cast is needed
      // because the union's input type is narrower than the flat defaults.
      onSubmit:
        datasourceFormSchema as unknown as typeof datasourceFormSchema & {
          "~standard": { types: { input: DatasourceFormDefaults } };
        },
    },
    onSubmit: async ({ value }) => onSubmit(value),
  });

export type DatasourceForm = ReturnType<typeof useDatasourceForm>;

// ---------------------------------------------------------------------------
// Payload builder — maps flat form values to the API shape
// ---------------------------------------------------------------------------

/**
 * Derives a default datasource name from the flat form values, scoped to the
 * currently selected type. Returns an empty string when nothing usable is
 * available — callers should treat that as "leave the name field alone".
 *
 * Strategy per driver:
 * - SQL drivers + Snowflake → `database`
 * - Databricks → `catalog`
 * - ClickHouse → `chDatabase` if filled, else first hostname label of `url`
 * - BigQuery → `project_id` parsed from the service account JSON
 */
export const deriveDatasourceName = (
  v: DatasourceFormDefaults,
  type: DatasourceTypeKey | "",
): string => {
  switch (type) {
    case "POSTGRESQL":
    case "MYSQL":
    case "REDSHIFT":
    case "SNOWFLAKE":
      return v.database.trim();
    case "DATABRICKS":
      return v.catalog.trim();
    case "CLICKHOUSE": {
      const ch = v.chDatabase.trim();
      if (ch) return ch;
      const raw = v.url.trim();
      if (!raw) return "";
      try {
        const u = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
        return u.hostname.split(".")[0] ?? "";
      } catch {
        return "";
      }
    }
    case "BIGQUERY": {
      const raw = v.serviceAccount.trim();
      if (!raw) return "";
      try {
        const parsed = JSON.parse(raw);
        return typeof parsed?.project_id === "string"
          ? parsed.project_id.trim()
          : "";
      } catch {
        return "";
      }
    }
    default:
      return "";
  }
};

/** The Snowflake form fields that carry authentication material. */
export type SnowflakeAuthValues = Pick<
  DatasourceFormDefaults,
  "authMethod" | "password" | "privateKey" | "privateKeyPass"
>;

/**
 * Picks the credential fields for the selected Snowflake auth method, so a
 * connection never carries both a password and a private key.
 */
export const snowflakeAuthCredentials = (v: SnowflakeAuthValues) =>
  v.authMethod === "keypair"
    ? {
        privateKey: v.privateKey,
        ...(v.privateKeyPass ? { privateKeyPass: v.privateKeyPass } : {}),
      }
    : { password: v.password };

/** Maps flat form values to `{ type, credentials, options }` expected by the API. */
export const buildCreatePayload = (v: DatasourceFormDefaults) => {
  const type = v.type as DatasourceTypeKey;

  switch (type) {
    case "POSTGRESQL":
    case "MYSQL":
    case "REDSHIFT":
      return {
        type,
        credentials: {
          host: v.host,
          port: Number(v.port),
          database: v.database,
          user: v.user,
          password: v.password,
        },
        options: v.ssl ? { ssl: true as const } : undefined,
      };

    case "BIGQUERY":
      return {
        type,
        credentials: {
          serviceAccount: v.serviceAccount,
        },
      };

    case "CLICKHOUSE":
      return {
        type,
        credentials: {
          url: v.url,
          username: v.username,
          password: v.password,
          ...(v.chDatabase ? { database: v.chDatabase } : {}),
        },
      };

    case "SNOWFLAKE":
      return {
        type,
        credentials: {
          account: v.account,
          warehouse: v.warehouse,
          database: v.database,
          schemas: v.schemas,
          username: v.username,
          ...snowflakeAuthCredentials(v),
          ...(v.role ? { role: v.role } : {}),
        },
      };

    case "DATABRICKS":
      return {
        type,
        credentials: {
          host: v.host,
          path: v.path,
          token: v.token,
          catalog: v.catalog,
        },
      };
  }
};
